import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
// --- Import components
import Input from "../../../../components/UI/Input/Input";
import Preloader from "../../../../components/UI/Preloader/Preloader";
// --- Import hooks
import useMakeRequest from "../../../../hooks/useMakeRequest";
import rsa from "../../../../hooks/crypt";
import useAppendJs from "../../../../hooks/useAppendJs";
// --- Import fucntions
import pixelEvent from "../../../../hooks/pixelEvent";
// --- Import images
import GenericCard from "../../../../assets/images/generic_card.png";
import AmexCard from "../../../../assets/images/amex_card.png";
import DiscoverCard from "../../../../assets/images/discover_card.png";
import MastercardCard from "../../../../assets/images/mastercard_card.png";
import VisaCard from "../../../../assets/images/visa_card.png";

import "./ChangePaymentMethod.sass";
import ModalWindow from "../../../../components/UI/ModalWindow/ModalWindow";

function makeGetRequest(url, callback) {
  const httpRequest = new XMLHttpRequest();

  if (!httpRequest) {
    return;
  }
  httpRequest.onreadystatechange = () => {
    if (httpRequest.readyState === XMLHttpRequest.DONE) {
      if (httpRequest.status === 200) {
        callback(httpRequest.responseText);
      } else {
        callback({ status: "error", msg: "JS Ajax problem" });
      }
    }
  };
  httpRequest.open("GET", url);
  httpRequest.setRequestHeader(
    "Content-Type",
    "application/json;charset=UTF-8"
  );
  httpRequest.send();
}
function makePostRequest(url, data, callback) {
  const httpRequest = new XMLHttpRequest();

  if (!httpRequest) {
    return;
  }
  httpRequest.onreadystatechange = () => {
    if (
      httpRequest.readyState === XMLHttpRequest.DONE ||
      httpRequest.readyState === 4
    ) {
      if (httpRequest.status === 200) {
        callback(httpRequest.responseText, data);
      } else {
        callback('{"state":"error","result":"JS Ajax problem"}', data);
      }
    }
  };
  if (httpRequest.upload && httpRequest.upload.addEventListener) {
    httpRequest.upload.addEventListener("error", () => {
      callback('{"state":"error","result":"Post request failed"}', data);
    });
    httpRequest.upload.addEventListener("abort", () => {
      callback('{"state":"error","result":"Post request canceled"}', data);
    });
  }

  httpRequest.open("POST", url);
  httpRequest.setRequestHeader(
    "Content-Type",
    "application/json;charset=UTF-8"
  );
  httpRequest.send(JSON.stringify(data));
}

// ---- Function to check is valid card number
function luhnCheck(cardNumber) {
  // Luhn Check Code from https://gist.github.com/4075533
  // accept only digits, dashes or spaces
  const numericDashRegex = /^[\d\-\s]+$/;
  if (!numericDashRegex.test(cardNumber)) return false;

  // The Luhn Algorithm. It's so pretty.
  let nCheck = 0;
  let nDigit = 0;
  let bEven = false;
  const strippedField = cardNumber.replace(/\D/g, "");

  for (let n = strippedField.length - 1; n >= 0; n -= 1) {
    const cDigit = strippedField.charAt(n);
    nDigit = parseInt(cDigit, 10);
    // eslint-disable-next-line
    if (bEven && (nDigit *= 2) > 9) {
      nDigit -= 9;
    }

    nCheck += nDigit;
    bEven = !bEven;
  }

  return nCheck % 10 === 0;
}
// ---- Function to get last four digits of card number
function getLastFourDigits(data) {
  return data.replace(" ", "").slice(-4);
}
// ---- The function of obtaining a card image depending on the card number
function getCardImg(data) {
  let payCardImg = GenericCard;
  const regexMap = [
    { regEx: /^4[0-9]{5}/gi, cardType: VisaCard },
    { regEx: /^5[1-5][0-9]{4}/gi, cardType: MastercardCard },
    { regEx: /^3[47][0-9]{3}/gi, cardType: AmexCard },
    { regEx: /^(1800)/gi, cardType: DiscoverCard },
  ];

  const num = data.replace(" ", "");
  for (let i = 0; i < regexMap.length; i += 1) {
    if (num.match(regexMap[i].regEx)) {
      payCardImg = regexMap[i].cardType;
      break;
    }
  }

  return payCardImg;
}
// ---- Function to formatting fields
function fieldFormat(e, reg, separator, separatorStep) {
  const value = e.target.value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
  const matches = value.match(reg);
  const match = (matches && matches[0]) || "";
  const parts = [];

  for (let i = 0, len = match.length; i < len; i += separatorStep) {
    parts.push(match.substring(i, i + separatorStep));
  }

  if (parts.length) {
    return parts.join(separator);
  }
  return value;
}
const domain = process.env.NODE_ENV === "development" ? "http://pcapp.loc" : "";

function ChangePaymentMethod({ onClose, userData, setExpectedCard }) {
  const with3ds = useRef(false);
  const subscrId = useRef();
  const oid = useRef("718");
  const [setParams, result, isLoading, hasError, error] = useMakeRequest();
  const [value, setValue] = useState({ cardNum: "", exp: "", cvv: "" });
  const [showConfirm, setShowConfirm] = useState(false);
  const [cardNumError, setCardNumError] = useState("");
  const [expError, serExpError] = useState("");
  const [cvvError, serCvvError] = useState("");
  const [buttonStatus, setButtonSattus] = useState("available");

  const appendJs = useAppendJs();

  const getPostData = useCallback(() => {
    const postData = {
      product_key: "pcappstore_valid",
      free_products: "",
      pType: "cent",
      page: `offer#${oid.current}`, // offer# + offer from coockie
      thankyou: "/fa/activated_lic.php",
      guid: userData.guid,
      oid: oid.current,
      cardNumber: rsa.encrypt(
        document.getElementById("cardNum").value.replaceAll(/\s/g, "")
      ),
      exp: rsa.encrypt(document.getElementById("exp").value),
      cvv: rsa.encrypt(document.getElementById("cvv").value),
      name: rsa.encrypt(`${userData.first_name} ${userData.last_name}`),
      email: rsa.encrypt(userData.email.replace(/\s/gi, "")),
      phone: rsa.encrypt(userData.phone.replace(/[^0-9+]/gi, "")),
      to: new Date().getTimezoneOffset(),
      nocache: Date.now(),
      callback: {
        type: "js",
        value:
          "completePurchaseCallback(Object.assign(getPostData(), {%response%}), applyPaymentResponse);", // Global object needs to be called ccForm for applyPaymentResponse to work on callback
      },
      allow_duplicate: true,
    };

    if (subscrId) {
      postData.subscr_id = subscrId.current;
    }
    return postData;
  }, [value, userData, subscrId.current]);

  const sendCCForm = useCallback((data) => {
    setParams({
      c: "front",
      a: "bstransCC",
      p: data,
    });
  }, []);

  const duplicatedSubmitCCForm = useCallback(() => {
    const data = getPostData();
    data.allow_duplicate = true;
    sendCCForm(data);
  }, [getPostData, sendCCForm]);

  const postTheForm = useCallback(
    (params) => {
      pixelEvent({
        evt_src: "acct",
        evt_action: "submit_cc_form",
        oid: oid.current,
        guid: userData.guid,
      });

      let data = getPostData();

      // -- add params (3ds_id) into the post_data -- //
      /* eslint-disable-next-line valid-typeof */
      if (typeof params !== "undefined" && typeof params !== "Object") {
        data = Object.assign(data, params);
      }

      sendCCForm(data);
    },
    [sendCCForm, userData, getPostData]
  );

  const activate3dsSubmit = useCallback(() => {
    pixelEvent({
      evt_src: "acct",
      evt_action: "3ds_activate",
      oid: oid.current,
      guid: userData.guid,
    });
    const newCard = {
      ccNumber: value.cardNum.replaceAll(/\s/g, ""),
      expDate: value.exp,
      amount: parseFloat("1.00"),
      currency: "USD",
    };
    window.bluesnap.threeDsPaymentsSubmitData(newCard);
  }, [value, userData]);

  // ---- Function to display card number error
  function showCardNumError(data) {
    let isValid = true;
    if (data.length <= 0) {
      setCardNumError("Required");
      isValid = false;
    } else if (!luhnCheck(data)) {
      setCardNumError("Not valid");
      isValid = false;
    } else {
      setCardNumError("");
    }
    return isValid;
  }
  // ---- Function to display cvv error
  function showCvvError(data) {
    let isValid = true;
    if (data.length <= 0) {
      serCvvError("Required");
      isValid = false;
    } else if (data.length !== 3 && data.length !== 4) {
      serCvvError("Not valid");
      isValid = false;
    } else {
      serCvvError("");
    }
    return isValid;
  }
  // ---- Function to display exp error
  function showExpError(data) {
    let isValid = true;
    if (data.length <= 0) {
      serExpError("Required");
      isValid = false;
    } else if (
      parseInt(data.substring(0, 2), 10) > 12 ||
      parseInt(data.substring(3), 10) < 20 ||
      parseInt(data.substring(3), 10) > 50
    ) {
      serExpError("Not valid");
      isValid = false;
    } else {
      serExpError("");
    }
    return isValid;
  }

  // ---- Function to submit form if fields is valid
  const submitForm = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      if (
        showCardNumError(value.cardNum) &&
        showExpError(value.exp) &&
        showCvvError(value.cvv)
      ) {
        setButtonSattus("disabled");
        // send request with 3ds and take 1$
        if (with3ds.current) {
          activate3dsSubmit();
        } else {
          postTheForm();
        }
      }
    },
    [value, postTheForm, activate3dsSubmit]
  );

  const activate3ds = useCallback(
    (cb) => {
      let bsToken;
      function setup3ds() {
        const interval = setInterval(() => {
          if (typeof window.bluesnap !== "undefined" && bsToken) {
            clearInterval(interval);
            window.bluesnap.threeDsPaymentsSetup(bsToken, (sdkResponse) => {
              const { code } = sdkResponse;
              if (code === "1") {
                // on Success
                const { cardData, threeDSecure } = sdkResponse;
                const actionName =
                  threeDSecure.authResult === "AUTHENTICATION_SUCCEEDED"
                    ? "3ds_success"
                    : "3ds_canceled";

                pixelEvent({
                  evt_src: "acct",
                  evt_action: actionName,
                  oid: oid.current,
                  guid: userData.guid,
                  cardData: JSON.stringify(cardData),
                  threeDSecure: JSON.stringify(threeDSecure),
                });
                postTheForm({
                  threeDRefId: threeDSecure.threeDSecureReferenceId,
                });
              } else {
                // in case some errors occurred
                const errorsArray = sdkResponse.info.errors;
                if (errorsArray) {
                  pixelEvent({
                    evt_src: "acct",
                    evt_action: "3ds_error",
                    message: errorsArray.join(". "),
                    oid: oid.current,
                    guid: userData.guid,
                  });
                  /* eslint-disable-next-line no-alert */
                  alert(errorsArray.join(". "));
                }
                // in case some warnings occurred
                const warningsArray = sdkResponse.info.warnings;
                if (warningsArray) {
                  pixelEvent({
                    evt_src: "acct",
                    evt_action: "3ds_warning",
                    message: warningsArray.join(". "),
                    oid: oid.current,
                    guid: userData.guid,
                  });
                  /* eslint-disable-next-line no-alert */
                  alert(warningsArray.join(". "));
                }
              }
            });
            cb();
          }
        }, 10);
      }

      // --- Get Bluesnap pfToken --- //
      makeGetRequest(
        `${domain}/api/api.php?c=front&a=bstoken&p[guid]=${userData.guid}`,
        (response) => {
          const oResponse = JSON.parse(response);
          if (oResponse.result) {
            bsToken = oResponse.result;
          } else {
            /* eslint-disable-next-line no-alert */
            alert("The page is not loaded correctly. Please reload it");
          }
        }
      );
      // === Get Bluesnap pfToken === //

      // --- Load Bluesnap script --- //
      makeGetRequest(
        `${domain}/api/api.php?c=front&a=bsurl&p[guid]=${userData.guid}`,
        (response) => {
          const oResponse = JSON.parse(response);
          if (oResponse.result) {
            const bsHost = oResponse.result;
            const scriptTag = document.createElement("script");
            scriptTag.src = `${bsHost}/web-sdk/4/bluesnap.js`;
            scriptTag.onload = setup3ds;
            document.body.appendChild(scriptTag);
          } else {
            /* eslint-disable-next-line no-alert */
            alert("The page is not loaded correctly. Please reload it");
          }
        }
      );
      // === Load Bluesnap script === //
    },
    [userData, submitForm]
  );

  // ---- Function to formatting card number field
  function numberFormat(e) {
    return setValue({ ...value, cardNum: fieldFormat(e, /\d{4,16}/g, " ", 4) });
  }
  // ---- Function to formatting cvv field
  function cvvFormat(e) {
    return setValue({ ...value, cvv: fieldFormat(e, /\d{4}/g, "", 4) });
  }
  // ---- Function to formatting exp field
  function expFormat(e) {
    return setValue({ ...value, exp: fieldFormat(e, /\d{2}\d{1,2}/g, "/", 2) });
  }
  // ----If request result is true then raise the state

  function completePurchaseCallback(postData, callback) {
    let data = postData;
    if (typeof data === "undefined") {
      data = {};
    }
    makePostRequest(
      "/api/api.php",
      { c: "front/purchase/purchase", a: "complete", p: postData },
      (response) => {
        if (typeof callback === "function") {
          callback(response, postData);
        }
      }
    );
  }

  function applyPaymentResponse(response, data) {
    if (typeof response === "object") {
      pixelEvent({
        evt_src: "fa_offer",
        evt_action: "alert",
        message: "Response is object or empty",
        oid: oid.current,
        guid: userData.guid,
      });
      setButtonSattus("available");
      return;
    }
    const oResponse = JSON.parse(response);
    if (
      !Object.prototype.hasOwnProperty.call(oResponse, "state") ||
      !Object.prototype.hasOwnProperty.call(oResponse, "result")
    ) {
      setButtonSattus("available");
      return;
    }
    if (oResponse.state === "error") {
      const alertMessage = oResponse.result;
      if (alertMessage === "Duplicate of purchase") {
        if (
          // eslint-disable-next-line no-alert
          window.confirm(
            "You have already purchased this, would you like to purchase again?"
          )
        ) {
          duplicatedSubmitCCForm();
        } else {
          setButtonSattus("available");
          window.setTimeout(() => {
            window.open(data.thankyou, "blank");
          }, 200);
        }
      } else if (
        alertMessage.indexOf("https") !== -1 ||
        alertMessage.indexOf("http") !== -1
      ) {
        const responseDomain = alertMessage.match(/^.*\/\/[^\/]+/g); // eslint-disable-line no-useless-escape
        const link = responseDomain
          ? alertMessage.replace(responseDomain, "")
          : alertMessage;
        window.open(link, "blank");
      } else {
        pixelEvent({
          evt_src: "fa_offer",
          evt_action: "alert",
          message: alertMessage,
          oid: oid.current,
          guid: userData.guid,
        });
      }
      setButtonSattus("available");
      return;
    }
    // oResponse.state == "ok"
    if (
      typeof oResponse.result.message !== "undefined" &&
      typeof oResponse.result.message[0].errorName !== "undefined"
    ) {
      const alertMessage = oResponse.result.message[0].description;
      pixelEvent({
        evt_src: "fa_offer",
        evt_action: "alert",
        message: alertMessage,
        oid: oid.current,
        guid: userData.guid,
      });
      setButtonSattus("available");
    } else if (typeof oResponse.result["3ds"] !== "undefined") {
      // send to 3ds //
      with3ds.current = true;
      activate3ds(() => {
        if (window.bluesnap.paymentRequestSupported()) {
          submitForm();
        } else {
          pixelEvent({
            evt_src: "acct",
            evt_action: "3ds_canceled",
            oid: oid.current,
            guid: userData.guid,
            message: "Browser does not support 3DS",
          });
          with3ds.current = false;
          submitForm();
        }
      });
      subscrId.current = result.subscr_id;
    } else if (typeof oResponse.result["3ds_browser"] !== "undefined") {
      pixelEvent({
        evt_src: "fa_offer",
        evt_action: "open_browser_3ds",
        oid: oid.current,
        guid: userData.guid,
      });
      setButtonSattus("available");
    } else if (
      oResponse.result &&
      oResponse.result.type === "js" &&
      typeof oResponse.result.body !== "undefined"
    ) {
      appendJs(oResponse.result.body);
    } else {
      // suceess //
      pixelEvent({
        evt_src: "fa_offer",
        evt_action: "paid_success",
        oid: oid.current,
        guid: userData.guid,
        amount: 0.01,
      });
      setButtonSattus("available");
      onClose();
      setExpectedCard(getLastFourDigits(value.cardNum));
    }
  }

  useEffect(() => {
    if (result && isLoading === false && hasError === false) {
      if (typeof result["3ds"] !== "undefined") {
        // send to 3ds //
        with3ds.current = true;
        activate3ds(() => {
          if (window.bluesnap.paymentRequestSupported()) {
            submitForm();
          } else {
            pixelEvent({
              evt_src: "acct",
              evt_action: "3ds_canceled",
              oid: oid.current,
              guid: userData.guid,
              message: "Browser does not support 3DS",
            });
            with3ds.current = false;
            submitForm();
          }
        });
        subscrId.current = result.subscr_id;
      } else if (result.type === "js" && typeof result.body !== "undefined") {
        appendJs(result.body);
      } else {
        setButtonSattus("available");
        onClose();
        setExpectedCard(getLastFourDigits(value.cardNum));
      }
    }
  }, [
    result,
    with3ds.current,
    subscrId.current,
    isLoading,
    hasError,
    value.cardNum,
  ]);

  useEffect(() => {
    if (!isLoading && hasError && error) {
      setButtonSattus("available");
    }
    if (!isLoading && hasError && error === "Duplicate of purchase") {
      setShowConfirm(true);
    }
  }, [hasError, error, isLoading]);

  useEffect(() => {
    if (
      result &&
      !isLoading &&
      !hasError &&
      result.type === "js" &&
      typeof result.body !== "undefined"
    ) {
      window.completePurchaseCallback = completePurchaseCallback;
      window.applyPaymentResponse = applyPaymentResponse;
      window.getPostData = getPostData;
    }
  }, [
    result,
    isLoading,
    hasError,
    completePurchaseCallback,
    getPostData,
    applyPaymentResponse,
  ]);

  return (
    <>
      <div className="col-12">
        <form
          onSubmit={submitForm}
          autoComplete="off"
          className="row flex-column flex-md-row align-items-start align-items-md-center  change-payment-method"
        >
          <>
            <div className="col-12 col-md-auto pr-md-1 change-payment-method__cell">
              <div className="change-payment-method__card-type">
                <img
                  src={getCardImg(value.cardNum)}
                  alt=""
                  className=" change-payment-method__card-img"
                />
              </div>
              <Input
                wrapperClass="change-payment-method__input-wrapper change-payment-method__input-wrapper--number"
                className="change-payment-method__input"
                errorClass="change-payment-method__input-error"
                placeholder="XXXX XXXX XXXX XXXX"
                value={value.cardNum}
                id="cardNum"
                onChange={(e) => numberFormat(e)}
                error={cardNumError}
                autofocus
              />
            </div>
            <div className="col-12 col-md-auto change-payment-method__cell change-payment-method__cell--big">
              <div className="change-payment-method__fields">
                <Input
                  wrapperClass="change-payment-method__input-wrapper change-payment-method__input-wrapper--date"
                  className="change-payment-method__input change-payment-method__input--date"
                  errorClass="change-payment-method__input-error"
                  placeholder="MM/YY"
                  value={value.exp}
                  id="exp"
                  onChange={(e) => expFormat(e)}
                  error={expError}
                />
                <Input
                  wrapperClass="change-payment-method__input-wrapper"
                  className="change-payment-method__input change-payment-method__input--cvv"
                  errorClass="change-payment-method__input-error"
                  placeholder="CVC"
                  value={value.cvv}
                  id="cvv"
                  onChange={(e) => cvvFormat(e)}
                  error={cvvError}
                />
              </div>
              <button
                type="submit"
                disabled={buttonStatus === "disabled"}
                className={`change-payment-method__btn ${
                  buttonStatus === "disabled"
                    ? "change-payment-method__btn--disabled"
                    : ""
                }`}
              >
                {buttonStatus === "disabled" ? (
                  <Preloader
                    className="change-payment-method__loading"
                    width="20px"
                  />
                ) : (
                  "Save"
                )}
              </button>
            </div>
            {hasError && (
              <div className="change-payment-method__error">
                {error && <p>{error}</p>}
              </div>
            )}
          </>
        </form>
      </div>
      <ModalWindow
        title="You have already purchased this, do you want to purchase again?"
        isOpen={showConfirm}
        onCancel={() => setShowConfirm(false)}
      >
        <div className="duplicate-confirm__row">
          <button
            className="duplicate-confirm__button"
            onClick={() => {
              duplicatedSubmitCCForm();
              setShowConfirm(false);
            }}
          >
            Yes
          </button>
          <button
            className="duplicate-confirm__button"
            onClick={() => {
              setShowConfirm(false);
            }}
          >
            No
          </button>
        </div>
      </ModalWindow>
    </>
  );
}

const mapStateToProps = (state) => ({
  userData: state.userDataStore.userData,
});

const mapDispatchToProps = {};

ChangePaymentMethod.propTypes = {
  userData: PropTypes.objectOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  setExpectedCard: PropTypes.func.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangePaymentMethod);
